import { DragDropEvent } from "services/dragdrop/DragDropContext";
import { EventsByResourceType, EventsListType, EventType, ResourceGroupType, SchedulerDraggableId, SchedulerDroppableId } from "../SchedulingTypes";
import { saveData } from "services/api/saveData";
import { convertLocalDateTimeToUTC } from "internationalization/timezoneConversions";
import { convertDayKeyAndTimeSlotToEndDateTimeString, convertDayKeyAndTimeSlotToStartDateTimeString, generateTimeKeyFromDateTimeString } from "./schedulingDateUtils";
import { formatDate } from "services/utils/dateTimeUtils";
import { t } from "i18next";
import { DayPassCheckItem } from "services/subcontractors/useSubcontractorDayPassCheck";

type ResolvedPosition = {
    resourceId: string | number;
    day: string;
    startTime: string;
};

// Resolve the drop target
export const resolveDropTarget = (
    droppable: SchedulerDroppableId,
    fallbackStartTime: string
): ResolvedPosition | null => {

    if (droppable.type === 'timeSlot') {
        return {
            resourceId: droppable.resourceId,
            day: droppable.day,
            startTime: droppable.timeSlot
        };
    }

    if (droppable.type === 'dayList') {
        return {
            resourceId: droppable.resourceId,
            day: droppable.day,
            startTime: fallbackStartTime
        };
    }

    if (droppable.type === 'timeBlock') {
        return {
            resourceId: droppable.resourceId,
            day: droppable.day,
            startTime: droppable.timeBlock
        };
    }

    return null;
};

export const handleAppointmentDrag = async (
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>,
    events: EventsListType,
    resourceGroups: ResourceGroupType[],
    userLocale: string,
    userTimezone: string,
    setFloatingAlert: Function,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
    checkDayPasses?: (items: DayPassCheckItem[]) => Promise<boolean>,
    handleDayPassError?: (error: any) => boolean
) => {

    // Unpack the dragresult
    const { source, destination, draggableId } = dragResult;

    // Handle appointment event drag
    if (source &&
        destination &&
        source.droppableId &&
        destination.droppableId &&
        draggableId.type === 'appointment'
    ) {
        // Get the event from the draggable object
        const event = draggableId.event;

        if (event && event.appointmentId && event.startTime) {

            // Get the from and to position
            const from = resolveDropTarget(source.droppableId, event.startTime);
            const to = resolveDropTarget(destination.droppableId, event.startTime);

            if (from && to) {
                // Determine if the event is dragged to the same position
                const isSamePosition = (
                    from.resourceId === to.resourceId &&
                    from.day === to.day &&
                    from.startTime === to.startTime
                );

                // When dragged to the same position, do nothing
                if (isSamePosition) return;

                // Update the event drag to the backend
                await updateEventAndAppointmentAfterDrag(
                    event,
                    resourceGroups,
                    events,
                    from.resourceId,
                    to.resourceId,
                    to.day,
                    to.startTime,
                    userLocale,
                    userTimezone,
                    setEvents,
                    setEventsByResource,
                    setFloatingAlert,
                    checkDayPasses,
                    handleDayPassError
                );
            }
        }
    }
};

// Updates the event and appointment after drag
const updateEventAndAppointmentAfterDrag = async (
    draggableEvent: EventType,
    resourceGroups: ResourceGroupType[],
    events: EventsListType,
    sourceResourceId: string | number,
    destinationResourceId: string | number,
    destinationDay: string,
    destinationStartTime: string,
    userLocale: string,
    userTimezone: string,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
    setFloatingAlert: Function,
    checkDayPasses?: (items: DayPassCheckItem[]) => Promise<boolean>,
    handleDayPassError?: (error: any) => boolean
) => {
    // Get the original event
    const eventKey = draggableEvent.key;
    const originalEvent = events[eventKey];

    // Only allow updating of appointment events
    if (draggableEvent.type !== 'appointment' || originalEvent.type !== 'appointment') return;

    const duration = originalEvent.durationMinutes ?? 60;

    // Warn and stop when the destination subcontractor has no day pass for the new day
    const destinationResource = resourceGroups.flatMap(group => group.resources).find(res => String(res.id) === String(destinationResourceId));
    const resourceOrDayChanged = String(sourceResourceId) !== String(destinationResourceId) || originalEvent.dayKey !== destinationDay;
    if (checkDayPasses && destinationResource?.is_subcontractor_role && resourceOrDayChanged) {
        const plannedStart = convertLocalDateTimeToUTC(convertDayKeyAndTimeSlotToStartDateTimeString(destinationDay, destinationStartTime, userLocale, userTimezone), userLocale, userTimezone);
        const plannedEnd = convertLocalDateTimeToUTC(convertDayKeyAndTimeSlotToEndDateTimeString(destinationDay, destinationStartTime, duration, userLocale, userTimezone), userLocale, userTimezone);
        const mayContinue = await checkDayPasses([{ resource_id: destinationResourceId, start_datetime: plannedStart, end_datetime: plannedEnd, timezone: userTimezone }]);
        if (!mayContinue) return;
    }

    // Delete the event from the former resource
    setEventsByResource(prev => {
        const updated = { ...prev };
        updated[sourceResourceId] = updated[sourceResourceId].filter(key => key !== eventKey);
        return updated;
    });

    // Convert the day keys and timeslots to local datetime objects
    const convertedStartDateTime = convertDayKeyAndTimeSlotToStartDateTimeString(destinationDay, destinationStartTime, userLocale, userTimezone);
    const convertedEndDateTime = convertDayKeyAndTimeSlotToEndDateTimeString(destinationDay, destinationStartTime, duration, userLocale, userTimezone);

    // Update the event with the new resouce, day and timeslot
    const updatedEvent = {
        ...originalEvent,
        resourceId: destinationResourceId,
        dayKey: destinationDay,
        timeSlot: destinationStartTime,
        startDateTime: convertedStartDateTime,
        endDateTime: convertedEndDateTime,
        startTime: generateTimeKeyFromDateTimeString(convertedStartDateTime),
        endTime: generateTimeKeyFromDateTimeString(convertedEndDateTime)
    };

    // Update the events state
    setEvents(prev => ({
        ...prev,
        [eventKey]: updatedEvent,
    }));

    // Add the event to the new resource state
    setEventsByResource(prev => {
        const newEventsByResource = { ...prev };

        // Create a new array for the destination resource if it doesn't exists
        if (!newEventsByResource[destinationResourceId]) {
            newEventsByResource[destinationResourceId] = [];
        }

        // Add the event key to the resource
        newEventsByResource[destinationResourceId].push(eventKey);

        return newEventsByResource;
    });

    // Convert the local datetimes to utc datetimes
    const utcStartDateTime = convertLocalDateTimeToUTC(convertedStartDateTime, userLocale, userTimezone);
    const utcEndDateTime = convertLocalDateTimeToUTC(convertedEndDateTime, userLocale, userTimezone);

    // Configure the submit data for the appointment
    const updatedAppointment = {
        id: originalEvent.appointmentId,
        uuid: null,
        job: originalEvent.job?.id,
        new_resource: destinationResourceId,
        old_resource: sourceResourceId,
        timezone: userTimezone,
        start_datetime: utcStartDateTime,
        end_datetime: utcEndDateTime,
    }

    // Restores the event on its original position when the save was refused
    const revertEvent = () => {
        setEvents(prev => ({ ...prev, [eventKey]: originalEvent }));
        setEventsByResource(prev => {
            const restored = { ...prev };
            restored[destinationResourceId] = (restored[destinationResourceId] || []).filter(key => key !== eventKey);
            restored[sourceResourceId] = [...(restored[sourceResourceId] || []), eventKey];
            return restored;
        });
    };

    // Save the appointment, a refusal for missing day passes shows the modal
    const response = await saveData({
        apiUrl: `patch_appointment/${draggableEvent.appointmentId}`,
        method: 'patch',
        data: updatedAppointment
    }).catch((error) => {
        revertEvent();
        if (!(handleDayPassError && handleDayPassError(error))) {
            setFloatingAlert({ type: 'danger', message: t('scheduling.scheduling_board.appointment_save_failed_alert') });
        }
        return undefined;
    });
    if (!response) return;

    // Find the name of the resource based on the destination resource id
    const findResourceName = (resourceId: number | string): string | null => {
        for (const group of resourceGroups) {
            const resource = group.resources.find(res => res.id === resourceId);
            if (resource) {
                return resource.name
            }
        }

        return '';
    };

    // Show floating alert on success
    if (response?.status === 200) {
        setFloatingAlert({
            type: 'success',
            message: t('scheduling.scheduling_board.appointment_dragged_to_resource_alert', { resource_name: findResourceName(destinationResourceId), date: formatDate(destinationDay, userLocale, 'writtenMonth'), time: destinationStartTime })
        });
    }
}